import classNames from 'clsx'
import { arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types'
import { Fragment, PureComponent } from 'react'

import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import Button from 'views/components/atoms/Button'
import IconRefresh from 'views/components/atoms/Icon/IconRefresh'
import Input from 'views/components/atoms/Input'
import Slider from 'views/components/atoms/Slider'
import { COMPONENT_SIZE, ICON_SIZE } from 'views/consts'

import 'views/components/molecules/InputRange/InputRange.scss'

/**
 * Refactored version of the InputRange:
 *  + Converted to TS
 *  + Redundant props and event handlers dropped
 */
class InputRange extends PureComponent<ComponentProps> {
  getInputNames(): Record<string, string> {
    const { name } = this.props

    return {
      min: `${name}__min`,
      max: `${name}__max`,
      button: `${name}__button`,
      rheostat: `${name}__rheostat`,
    }
  }

  renderLabel(inputNames: Record<string, string>) {
    const { label, size } = this.props

    const labelClassNames = classNames(
      'l-flex u-font-weight--bold',
      `u-margin-${size}--bottom`,
      {
        'a-h3': size === 'large',
        'a-h4': size === 'default',
        'a-h5': size === 'small',
      }
    )

    return (
      <label className={labelClassNames} htmlFor={inputNames.min}>
        {label}
      </label>
    )
  }

  getRheostatValues(): number[] {
    const {
      values: [min, max],
      range: [rangeMin, rangeMax],
    } = this.props

    return [min >= rangeMin ? min : rangeMin, max <= rangeMax ? max : rangeMax]
  }

  render() {
    const {
      rounded,
      label,
      size,
      iconSize,
      placeholders,
      delimiterLabel,
      events: {
        onRheostatUpdate,
        onMinChange,
        onMaxChange,
        onMinKeyUp,
        onMaxKeyUp,
      },
      values: [min, max],
      range: [rangeMin, rangeMax],
      symbol,
      hideUpdateButton,
      rheostatClassName,
      noPadding,
    } = this.props

    const inputNames = this.getInputNames()
    const inputClassNames = classNames(
      'm-InputRange__input u-font-weight--normal u-no-margin u-border',
      `u-padding-${size}`,
      {
        'u-border-radius': rounded,
        'm-InputRange__input--with-symbol': symbol,
      }
    )

    let WrapperComponent
    let wrapperProps
    let labelRendered

    if (label) {
      WrapperComponent = Fragment
      wrapperProps = {}
      labelRendered = this.renderLabel(inputNames)
    } else {
      WrapperComponent = 'label'
      wrapperProps = {
        htmlFor: inputNames.min,
        className: 'o-InputRange__wrapper',
      }
      labelRendered = ''
    }

    const symbolComponent = symbol ? (
      <span className="m-InputRange__symbol u-position--absolute u-transform-center--y">
        {symbol}
      </span>
    ) : null

    return (
      <WrapperComponent {...wrapperProps}>
        {labelRendered}
        <div className="l-flex l-flex--stretch-children u-margin-x-large--bottom@small-tiny m-InputRange__inputs-wrapper">
          <div className="u-margin--right l-flex__primary u-position--relative m-InputRange__input-from">
            <Input
              name={inputNames.min}
              className={inputClassNames}
              placeholder={placeholders?.min}
              value={min}
              type="number"
              disableAutoCompleteAndFriends
              onChange={onMinChange}
              onKeyUp={onMinKeyUp}
            />
            {symbolComponent}
          </div>
          {delimiterLabel && (
            <div className="m-InputRange__delimiter l-flex__center u-color--dark">
              {delimiterLabel}
            </div>
          )}
          <div
            className={classNames(
              'l-flex__primary u-position--relative m-InputRange__input-to',
              {
                'u-margin--right@medium': !noPadding,
              }
            )}
          >
            <Input
              name={inputNames.max}
              className={inputClassNames}
              placeholder={placeholders?.max}
              value={max}
              type="number"
              disableAutoCompleteAndFriends
              onChange={onMaxChange}
              onKeyUp={onMaxKeyUp}
            />
            {symbolComponent}
          </div>
          {hideUpdateButton ? null : (
            <Button
              name={inputNames.button}
              className="m-InputRange__button u-hidden@small-tiny"
              rounded={rounded}
              inline
              reverse
            >
              <IconRefresh size={iconSize} />
            </Button>
          )}
        </div>
        <div
          className={classNames('m-InputRange__rheostat', rheostatClassName)}
        >
          <Slider
            max={rangeMax}
            min={rangeMin}
            onChange={(_, values) => onRheostatUpdate({ values })}
            step={0.01}
            value={this.getRheostatValues()}
          />
        </div>
      </WrapperComponent>
    )
  }
}

const propTypes = {
  name: string.isRequired,
  values: arrayOf(number.isRequired).isRequired,
  range: arrayOf(number.isRequired).isRequired,
  events: shape({
    onMaxChange: func.isRequired,
    onMaxKeyUp: func.isRequired,
    onMinChange: func.isRequired,
    onMinKeyUp: func.isRequired,
    onRheostatUpdate: func.isRequired,
  }).isRequired,
  iconSize: oneOf(ICON_SIZE),
  size: oneOf(COMPONENT_SIZE),
  label: string,
  rounded: bool,
  placeholders: shape({
    min: string,
    max: string,
  }),
  symbol: string,
  delimiterLabel: string,
  decimalPlaces: number,
  hideUpdateButton: bool,
  noPadding: bool,
  rheostatClassName: string,
}

const defaultProps = {
  placeholders: {
    min: '',
    max: '',
  },
  iconSize: 'small',
  size: 'small',
  rounded: true,
  label: null,
  symbol: null,
  decimalPlaces: 0,
  delimiterLabel: null,
  hideUpdateButton: false,
  noPadding: false,
  rheostatClassName: 'u-hidden@medium',
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

export default InputRange
